import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NormalizedEmployee, ChannelType, InstoreState } from '../../utils/employee.model';
import { isEmployeeInactive } from '../../utils/board-user-utils';

/**
 * Board configuration interface for multi-channel settings.
 */
export interface BoardConfig {
  showDelivery?: boolean;
  enablePhoneChannel?: boolean;
  enableOnlineChannel?: boolean;
  allowUpToTakePhone?: boolean;
  allowUpToTakeInternet?: boolean;
  allowOnDeckToTakePhone?: boolean;
  allowOnDeckToTakeInternet?: boolean;
  /** When true, single-channel boards display the modern card-based UI instead of legacy 5-column table */
  useModernLayout?: boolean;
}

/**
 * Action types available for employee state transitions.
 */
export type EmployeeActionType =
  | 'with_apt'
  | 'with_guest'
  | 'break'
  | 'return_from_break'
  | 'inactive'
  | 'make_available'
  | 'delivery'
  | 'complete_delivery_bottom'
  | 'complete_delivery_keep'
  | 'take_phone'
  | 'take_internet'
  | 'go_to_bottom'
  | 'complete_apt'
  | 'complete_apt_then_break'
  | 'go_to_bottom_then_break'
  | 'reclaim_position';

/**
 * Employee action event emitted when user selects an action.
 */
export interface EmployeeAction {
  type: EmployeeActionType;
  channel?: ChannelType;
  employee: NormalizedEmployee;
}

/**
 * Column type for modal context - extends ChannelType with 'with_guest'.
 */
export type ColumnType = ChannelType | 'with_guest';

/**
 * Action button configuration for modal display.
 */
export interface ActionButton {
  type: EmployeeActionType;
  label: string;
  icon: string;
  disabled: boolean;
  disabledReason?: string;
  channel?: ChannelType;
  /** When true, action requires admin authentication before execution */
  requiresAuth?: boolean;
  /** Visual variant: 'danger' for destructive/sensitive actions */
  variant?: 'danger';
}

/**
 * EmployeeActionModalComponent displays context-sensitive actions
 * for employee state transitions based on column, state, and board config.
 *
 * @example
 * <app-employee-action-modal
 *   [employee]="selectedEmployee"
 *   [column]="selectedColumn"
 *   [boardConfig]="boardConfig"
 *   [upEmployeeEmail]="upEmployee?.email"
 *   [onDeckEmployeeEmail]="onDeckEmployee?.email"
 *   [isVisible]="showActionModal"
 *   (actionSelected)="onActionSelected($event)"
 *   (closed)="onModalClosed()">
 * </app-employee-action-modal>
 */
@Component({
  selector: 'app-employee-action-modal',
  templateUrl: './employee-action-modal.component.html',
  styleUrls: ['./employee-action-modal.component.css']
})
export class EmployeeActionModalComponent implements OnChanges {
  /** The employee to show actions for */
  @Input() employee: NormalizedEmployee | null = null;

  /** The column where the employee was clicked */
  @Input() column: ColumnType = 'instore';

  /** Board configuration flags */
  @Input() boardConfig: BoardConfig = {};

  /** Email of the current UP employee (position 1 in instore) */
  @Input() upEmployeeEmail: string | null = null;

  /** Email of the current On Deck employee (position 2 in instore) */
  @Input() onDeckEmployeeEmail: string | null = null;

  /** Whether the modal is visible */
  @Input() isVisible = false;

  /** Emits when an action is selected */
  @Output() actionSelected = new EventEmitter<EmployeeAction>();

  /** Emits when the modal is closed without action */
  @Output() closed = new EventEmitter<void>();

  /** Computed list of available actions for current context */
  availableActions: ActionButton[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employee'] || changes['column'] || changes['boardConfig'] ||
        changes['upEmployeeEmail'] || changes['onDeckEmployeeEmail']) {
      this.computeAvailableActions();
    }
  }

  /**
   * Computes available actions based on employee state, column, and config.
   */
  computeAvailableActions(): void {
    if (!this.employee) {
      this.availableActions = [];
      return;
    }

    switch (this.column) {
      case 'instore':
        this.availableActions = this.getActionsForFloorUps(this.employee);
        break;
      case 'with_guest':
        this.availableActions = this.getActionsForWithGuest(this.employee);
        break;
      case 'phone':
        this.availableActions = this.getActionsForPhone(this.employee);
        break;
      case 'online':
        this.availableActions = this.getActionsForInternet(this.employee);
        break;
      default:
        this.availableActions = [];
    }
  }

   /**
    * Gets actions for Floor Ups column (instore employees NOT in with_guest).
    * Story 3.5.5: Implements state-specific context menu options per spec.
    */
   getActionsForFloorUps(employee: NormalizedEmployee): ActionButton[] {
     const actions: ActionButton[] = [];
     const instoreState = employee.channels.instore.state;

     // Check for global break state (breaks any state - AC #2)
     if (employee.globalState === 'break') {
       actions.push({
         type: 'return_from_break',
         label: 'Return from Break',
         icon: 'fa fa-sign-in-alt',
         disabled: false
       });
       actions.push({
         type: 'inactive',
         label: 'Make Inactive',
         icon: 'fa fa-ban',
         disabled: false
       });
       return actions;
     }

     switch (instoreState) {
       // AC #1: Floor Ups - Available
       case 'available':
         actions.push(
           { type: 'with_apt', label: 'With Appointment', icon: 'fa fa-calendar', disabled: false },
           { type: 'with_guest', label: 'With Guest', icon: 'fa fa-user', disabled: false },
           { type: 'break', label: 'Go on Break', icon: 'fa fa-coffee', disabled: false },
           { type: 'inactive', label: 'Make Inactive', icon: 'fa fa-ban', disabled: false }
         );
         break;

        // AC #2: Floor Ups - With Appointment
         case 'with_apt':
           // "Complete Appointment" option - keeps position in queue (like Return From Break)
           actions.push({
             type: 'complete_apt',
             label: 'Complete Appointment',
             icon: 'fa fa-check',
             disabled: false,
             channel: 'instore'
           });
           // "Go on Break" auto-completes appointment first (compound transition)
           actions.push({
             type: 'complete_apt_then_break',
             label: 'Go on Break',
             icon: 'fa fa-coffee',
             disabled: false
          });
          // Delivery option (conditional on feature flag)
          // Transitions to with_apt_delivery state - employee remains in Floor Ups column
          if (this.boardConfig.showDelivery) {
            actions.push({
              type: 'delivery',
              label: 'Delivery',
              icon: 'fa fa-truck',
              disabled: false
            });
          }
          actions.push({
            type: 'inactive',
            label: 'Make Inactive',
            icon: 'fa fa-ban',
            disabled: false
          });
          break;

       // AC #3: Floor Ups - With Guest (handled in getActionsForWithGuest)
       // Note: with_guest state is handled separately in With Guest column

        case 'with_apt_delivery':
          // Reclaim Position - returns to available, NO admin auth required
          // (unlike with_guest_delivery which requires admin approval)
          actions.push({
            type: 'complete_delivery_keep',
            label: 'Reclaim Position',
            icon: 'fa fa-arrow-left',
            disabled: false
          });
          break;
     }

     return actions;
   }

  /**
   * Gets actions for With Guest column (employees in with_guest states).
   * Story 3.5.5: AC #4 - With Guest state options
   */
  getActionsForWithGuest(employee: NormalizedEmployee): ActionButton[] {
    const actions: ActionButton[] = [];
    const instoreState = employee.channels.instore.state;

    switch (instoreState) {
      case 'with_guest':
        // AC #4: Go to Bottom of Floor Ups
        actions.push({
          type: 'go_to_bottom',
          label: 'Go to Bottom of Floor Ups',
          icon: 'fa fa-arrow-down',
          disabled: false,
          channel: 'instore'
        });
        
        // Reclaim Position - requires admin auth, matches legacy "Keep Place In Line" behavior
        actions.push({
          type: 'reclaim_position',
          label: 'Reclaim Position',
          icon: 'fa fa-arrow-left',
          disabled: false,
          channel: 'instore',
          requiresAuth: true,
          variant: 'danger'
        });
        
        // AC #4: Delivery option (conditional on feature flag)
        if (this.boardConfig.showDelivery) {
          actions.push({
            type: 'delivery',
            label: 'Delivery',
            icon: 'fa fa-truck',
            disabled: false
          });
        }
        
        // AC #4: Go on Break (compound transition: return to floor ups first, then break)
        actions.push({
          type: 'go_to_bottom_then_break',
          label: 'Go on Break',
          icon: 'fa fa-coffee',
          disabled: false
        });
        
        // AC #4: Make Inactive
        actions.push({
          type: 'inactive',
          label: 'Make Inactive',
          icon: 'fa fa-ban',
          disabled: false
        });
        break;

      case 'with_guest_delivery':
        actions.push({
          type: 'complete_delivery_bottom',
          label: 'Send To Bottom',
          icon: 'fa fa-arrow-down',
          disabled: false
        });
        // Reclaim Position - requires admin auth, matches legacy "Keep Place In Line" behavior
        actions.push({
          type: 'reclaim_position',
          label: 'Reclaim Position',
          icon: 'fa fa-arrow-left',
          disabled: false,
          channel: 'instore',
          requiresAuth: true,
          variant: 'danger'
        });
        break;
    }

    return actions;
  }

  /**
   * Gets actions for Phone Ups column.
   * Story 3.5.5: AC #5 - Phone Ups - Available state options
   * Removes "Take Phone Lead" and implements spec-compliant menu
   * 
   * If employee is on break, only show Return from Break (not Go on Break)
   */
  getActionsForPhone(employee: NormalizedEmployee): ActionButton[] {
    const actions: ActionButton[] = [];

    // If employee is on break, show break-specific menu (Return from Break, Make Inactive)
    if (employee.globalState === 'break') {
      actions.push({
        type: 'return_from_break',
        label: 'Return from Break',
        icon: 'fa fa-sign-in-alt',
        disabled: false
      });
      actions.push({
        type: 'inactive',
        label: 'Make Inactive',
        icon: 'fa fa-ban',
        disabled: false
      });
      return actions;
    }

    const unavailableReason = this.getUnavailabilityReason(employee, 'phone');

    // AC #5: Go to Bottom of Phone Queue
    actions.push({
      type: 'go_to_bottom',
      label: 'Go to Bottom of Phone Queue',
      icon: 'fa fa-arrow-down',
      disabled: !!unavailableReason,
      disabledReason: unavailableReason || undefined,
      channel: 'phone'
    });

    // AC #5: Go on Break
    actions.push({
      type: 'break',
      label: 'Go on Break',
      icon: 'fa fa-coffee',
      disabled: !!unavailableReason,
      disabledReason: unavailableReason || undefined
    });

    // AC #5: Make Inactive
    actions.push({
      type: 'inactive',
      label: 'Make Inactive',
      icon: 'fa fa-ban',
      disabled: false
    });

    return actions;
  }

  /**
   * Gets actions for Internet Ups column.
   * Story 3.5.5: AC #6 - Internet Ups - Available state options
   * Removes "Take Internet Lead" and implements spec-compliant menu
   * 
   * If employee is on break, only show Return from Break (not Go on Break)
   */
  getActionsForInternet(employee: NormalizedEmployee): ActionButton[] {
    const actions: ActionButton[] = [];

    // If employee is on break, show break-specific menu (Return from Break, Make Inactive)
    if (employee.globalState === 'break') {
      actions.push({
        type: 'return_from_break',
        label: 'Return from Break',
        icon: 'fa fa-sign-in-alt',
        disabled: false
      });
      actions.push({
        type: 'inactive',
        label: 'Make Inactive',
        icon: 'fa fa-ban',
        disabled: false
      });
      return actions;
    }

    const unavailableReason = this.getUnavailabilityReason(employee, 'online');

    // AC #6: Go to Bottom of Internet Queue
    actions.push({
      type: 'go_to_bottom',
      label: 'Go to Bottom of Internet Queue',
      icon: 'fa fa-globe',
      disabled: !!unavailableReason,
      disabledReason: unavailableReason || undefined,
      channel: 'online'
    });

    // AC #6: Go on Break
    actions.push({
      type: 'break',
      label: 'Go on Break',
      icon: 'fa fa-coffee',
      disabled: !!unavailableReason,
      disabledReason: unavailableReason || undefined
    });

    // AC #6: Make Inactive
    actions.push({
      type: 'inactive',
      label: 'Make Inactive',
      icon: 'fa fa-ban',
      disabled: false
    });

    return actions;
  }

  /**
   * Checks if employee is the UP for instore channel.
   */
  isUpForInstore(employee: NormalizedEmployee): boolean {
    return this.upEmployeeEmail === employee.email;
  }

  /**
   * Checks if employee is On Deck for instore channel.
   */
  isOnDeckForInstore(employee: NormalizedEmployee): boolean {
    return this.onDeckEmployeeEmail === employee.email;
  }

  /**
   * Gets the reason an employee is unavailable for a channel.
   */
  getUnavailabilityReason(employee: NormalizedEmployee, channel: ChannelType): string | null {
    if (employee.globalState === 'break') {
      return 'Employee is on break';
    }

    const instoreState = employee.channels.instore.state;
    if (instoreState === 'with_guest' || instoreState === 'with_apt' ||
        instoreState === 'with_guest_delivery' || instoreState === 'with_apt_delivery') {
      return 'Employee is busy instore';
    }

    // Story 5.3: Check global inactive state instead of per-channel
    if (isEmployeeInactive(employee)) {
      return 'Employee is inactive';
    }

    return null;
  }

  /**
   * Checks if Take Phone action is disabled.
   */
  isTakePhoneDisabled(employee: NormalizedEmployee, isUp: boolean, isOnDeck: boolean): boolean {
    // Check unavailability first
    if (this.getUnavailabilityReason(employee, 'phone')) {
      return true;
    }

    // Check board config restrictions
    if (isUp && this.boardConfig.allowUpToTakePhone === false) {
      return true;
    }
    if (isOnDeck && this.boardConfig.allowOnDeckToTakePhone === false) {
      return true;
    }

    return false;
  }

  /**
   * Gets the disabled reason for Take Phone action.
   */
  getTakePhoneDisabledReason(employee: NormalizedEmployee, isUp: boolean, isOnDeck: boolean): string | null {
    // Check unavailability first
    const unavailableReason = this.getUnavailabilityReason(employee, 'phone');
    if (unavailableReason) {
      return unavailableReason;
    }

    // Check board config restrictions
    if (isUp && this.boardConfig.allowUpToTakePhone === false) {
      return 'UP employee cannot take phone leads (board setting)';
    }
    if (isOnDeck && this.boardConfig.allowOnDeckToTakePhone === false) {
      return 'On Deck employee cannot take phone leads (board setting)';
    }

    return null;
  }

  /**
   * Checks if Take Internet action is disabled.
   */
  isTakeInternetDisabled(employee: NormalizedEmployee, isUp: boolean, isOnDeck: boolean): boolean {
    // Check unavailability first
    if (this.getUnavailabilityReason(employee, 'online')) {
      return true;
    }

    // Check board config restrictions
    if (isUp && this.boardConfig.allowUpToTakeInternet === false) {
      return true;
    }
    if (isOnDeck && this.boardConfig.allowOnDeckToTakeInternet === false) {
      return true;
    }

    return false;
  }

  /**
   * Gets the disabled reason for Take Internet action.
   */
  getTakeInternetDisabledReason(employee: NormalizedEmployee, isUp: boolean, isOnDeck: boolean): string | null {
    // Check unavailability first
    const unavailableReason = this.getUnavailabilityReason(employee, 'online');
    if (unavailableReason) {
      return unavailableReason;
    }

    // Check board config restrictions
    if (isUp && this.boardConfig.allowUpToTakeInternet === false) {
      return 'UP employee cannot take internet leads (board setting)';
    }
    if (isOnDeck && this.boardConfig.allowOnDeckToTakeInternet === false) {
      return 'On Deck employee cannot take internet leads (board setting)';
    }

    return null;
  }

  /**
   * Handles action button click.
   */
  selectAction(action: ActionButton): void {
    if (action.disabled || !this.employee) {
      return;
    }

    this.actionSelected.emit({
      type: action.type,
      channel: action.channel,
      employee: this.employee
    });
  }

  /**
   * Closes the modal.
   */
  close(): void {
    this.closed.emit();
  }

  /**
   * Prevents click propagation for modal content.
   */
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }
}
